// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/HR/RecruitmentOverviewTabs.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo } from "react";
import { useTabNavigation } from "../../helpers/useTabNavigation";
import { LayoutDashboard, Briefcase, Users, FileCheck } from 'lucide-react';
import { usePermission } from "../../hooks/usePermission"; 
import JobOpeningsList from "../../components/HRComponents/JobOpeningsList";
import CandidateList from "../../components/HRComponents/CandidateList";
import RecruitmentDashboard from "../../components/HRComponents/RecruitmentDashboard";
import MyAssignedInterviews from "../../components/HRComponents/MyAssignedInterviews";
import OfferList from "../../components/HRComponents/OfferList";
import OnboardingList from "../../components/HRComponents/OnboardingList";

const RecruitmentOverviewTabs = () => {
  const { hasPermission, user } = usePermission();

  const tabs = useMemo(() => {
    const allTabs = [

      {
        id: 'my-interviews',
        label: 'My Interviews',
        icon: <Users className="h-4 w-4" />,
        // description: 'My Scheduled Interviews',
        content: <MyAssignedInterviews user={user} />
      },
    ];

    // 4. Onboarding & Offer Approval Tab 
    const adminTabs = [
            // 1. Recruitment Dashboard Tab
      {
        id: 'overview',
        label: 'Overview',
        icon: <LayoutDashboard className="h-4 w-4" />,
        // description: 'Recruitment Funnel & Onboarding Statistics',
        content: <RecruitmentDashboard user={user} />,
        permissionKey: "hr:recruitment-overview", 
        action: "view"
        
      },
      // 2. Job Openings Tab
      {
        id: 'jobs',
        label: 'Job Openings',
        icon: <Briefcase className="h-4 w-4" />,
        // description: 'Manage Corporate Vacancies and Job Postings',
        content: <JobOpeningsList user={user} />,
        permissionKey: "hr:recruitment-jobs", 
        action: "view"
      },
      // 3. Candidates Directory Tab
      {
        id: 'candidates',
        label: 'Candidates',
        icon: <Users className="h-4 w-4" />,
        // description: 'Active Applicant Directory & Stage Transitions',
        content: <CandidateList user={user} />,
        permissionKey: "hr:recruitment-candidates", 
        action: "view"
      },
      {
        id: 'onboarding',
        label: 'Onboarding',
        icon: <Users className="h-4 w-4" />,
        // description: 'My Scheduled Interviews',
        content: <OnboardingList user={user} />,
        permissionKey: "hr:recruitment-onboarding", 
        action: "view"
      },
      {
        id: 'recruitment-offers',
        label: 'Offers',
        icon: <FileCheck className="h-4 w-4" />,
        // description: 'Offer Letters Dispatch',
        content: <OfferList user={user} />,
        permissionKey: "hr:recruitment-offers", 
        action: "view"
      },
    ];

    adminTabs.forEach(tab => {
      if (hasPermission(tab.permissionKey, tab.action)) {
        allTabs.push(tab);
      }
    });

    return allTabs;
  }, [user, hasPermission]);

  const { activeTab, changeTab } = useTabNavigation(tabs, 'overview');
  const activeTabData = tabs.find(tab => tab.id === activeTab) || tabs[0];

  return (
    <div className="bg-gray-50">
      {/* --- TOP TAB NAVIGATION --- */}
      <div className="bg-primary shadow-md sticky top-0 z-20">
        <div className="mx-auto">
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <nav className="flex space-x-2 overflow-x-auto custom-scrollbar pt-2" aria-label="Tabs">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => changeTab(tab.id)}
                      className={`
                        group inline-flex items-center py-3 px-6 font-bold text-[11px] uppercase tracking-[0.1em]
                        whitespace-nowrap transition-all duration-300 ease-in-out
                        ${isActive
                          ? 'bg-white text-primary rounded-t-xl transform -translate-y-px shadow-sm'
                          : 'text-red-100 hover:text-white hover:bg-red-700 rounded-t-xl'
                        }
                      `}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span className="mr-2 opacity-70">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="hidden lg:block text-[10px] font-bold text-red-100 uppercase tracking-widest opacity-80 italic">
                {activeTabData?.description}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- CONTENT AREA --- */}
      <div className="mx-auto px-4 py-6">
        <div className="animate-in fade-in duration-500">
          {activeTabData?.content}
        </div>
      </div>
    </div>
  );
};

export default RecruitmentOverviewTabs;